import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import ClearIcon from 'common/shared-ui/src/icons/ClearIcon';

import {
  getGantIssueCreate,
  getGantIssueCreateTrackers,
  getGantIssueEdit,
  getGantParentIssue,
  getGanttIssueUsers,
} from '../../../../../packages/common/api';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';
import { store } from '../../../app/redux';
import IssueFormField from '../../../issueCreateEdit/components/IssueFormField';
import GanttIssuePredecessor from '../GanttIssuePredecessor/GanttIssuePredecessor';

// Original class: rowAutocomlpete
const StyledRowAutocomlpete = styled(Autocomplete, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '&.MuiAutocomplete-root': {
    width: '100%',
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '0',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      right: 0,
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: '0',
      height: 'auto',
      ...(changedStyle && {
        color: '#212346',
      }),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      padding: 0,
      border: 'none',
    },
  },
  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
}));

const StyledAutocompletePopper = styled(Popper)(theme => ({
  zIndex: 9000,

  '& .MuiPaper-root.MuiAutocomplete-paper': {
    borderRadius: '0 0 16px 16px',
    border: '1px solid #E4E4EF',
    borderTop: 'none',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '& .MuiAutocomplete-noOptions': {
      padding: '8px 16px',
      color: '#212346',
      fontSize: '12px',
      lineHeight: '20px',
    },
    '& .MuiAutocomplete-listbox': {
      padding: '0',
      '& li': {
        padding: '8px 10px',
        whiteSpace: 'normal',
        fontSize: '12px',
        lineHeight: '20px',
        color: '#212346',
        '& .MuiButtonBase-root.MuiCheckbox-root': {
          padding: '2px',
          marginRight: '10px',
        },
        '&.Mui-focused': {
          backgroundColor: 'transparent',
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-selected': {
          backgroundColor: '#DCDFF4',
        },
      },
      '&::-webkit-scrollbar': {
        width: '2px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
  },
}));

const GanttEditCellUser = ({ cellProps, handleField }) => {
  const getLocalString = useTranslation();
  const [rowUsers, setRowUsers] = useState([]);
  useEffect(() => {
    let params = {
      row: Number(cellProps.row.id),
      group: Number(cellProps.row.project),
      project: Number(cellProps.row.project),
    };
    async function getUsers(params) {
      const { data: users } = await getGanttIssueUsers(params);
      setRowUsers(users);
      return users;
    }
    getUsers(params);
  }, [cellProps, setRowUsers]);
  return (
    <StyledRowAutocomlpete
      changedStyle={cellProps.row.changed}
      noOptionsText={getLocalString('labels', 'notFound')}
      onChange={(event, newValue) => {
        handleField(newValue, cellProps);
      }}
      //disablePortal
      options={rowUsers ? rowUsers : []}
      disableClearable
      renderOption={(props, option) => {
        return (
          <MenuItem
            {...props}
            key={option.valueId + option.valueName}
            value={option.valueId}
            selected={cellProps.row.services.userId === option.valueId}
          >
            {option.valueName}
          </MenuItem>
        );
      }}
      value={cellProps.value ? cellProps.value : ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionLabel={option => option.valueName || ''}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={getLocalString('labels', 'select')}
        />
      )}
      popupIcon={<ChevronIcon direction={'down'} />}
      slots={{
        popper: props => (
          <StyledAutocompletePopper
            {...props}
            sx={{ width: '200px!important', marginLeft: '-11px!important' }}
            placement='bottom-start'
          ></StyledAutocompletePopper>
        ),
      }}
    />
  );
};

export default GanttEditCellUser;
